import { lazy } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';

import { PortalAuthGuard, PortalGuestGuard } from '@/app/portal-guards';
import { PortalAuthLayout, PortalLayout } from '@/layouts/PortalLayout';
import { PORTAL_ROUTES } from '@/routes/paths';

/**
 * The customer portal route branch (Phase 6).
 *
 * A sibling of the ERP branch, not a child of it. It has its own guards, its own
 * layout, its own 404 and its own session store, so nothing about the internal
 * application — the module registry, the sidebar, the permission catalog — is in
 * scope for any screen below.
 *
 * These routes are registered **before** the ERP branch in the router, because
 * the ERP's authenticated branch ends in a `*` catch-all that would otherwise
 * swallow `/portal/*` and bounce a customer to the staff sign-in.
 */

const PortalLoginPage = lazy(() => import('@/pages/portal/PortalLoginPage'));
const PortalDashboardPage = lazy(() => import('@/pages/portal/PortalDashboardPage'));
const PortalProfilePage = lazy(() => import('@/pages/portal/PortalProfilePage'));
const PortalNotFoundPage = lazy(() => import('@/pages/portal/PortalNotFoundPage'));

const PortalQuotationsPage = lazy(() => import('@/pages/portal/PortalQuotationsPage'));
const PortalQuotationDetailPage = lazy(() =>
  import('@/pages/portal/PortalQuotationsPage').then((module) => ({
    default: module.PortalQuotationDetailPage,
  })),
);

const PortalSalesPage = lazy(() => import('@/pages/portal/PortalSalesPage'));
const PortalSaleDetailPage = lazy(() =>
  import('@/pages/portal/PortalSalesPage').then((module) => ({
    default: module.PortalSaleDetailPage,
  })),
);

const PortalPaymentsPage = lazy(() => import('@/pages/portal/PortalPaymentsPage'));

const PortalNotificationsPage = lazy(() => import('@/pages/portal/PortalNotificationsPage'));
const PortalPreferencesPage = lazy(() => import('@/pages/portal/PortalPreferencesPage'));

const PortalRentalsPage = lazy(() => import('@/pages/portal/PortalRentalsPage'));
const PortalRentalDetailPage = lazy(() =>
  import('@/pages/portal/PortalRentalsPage').then((module) => ({
    default: module.PortalRentalDetailPage,
  })),
);

const PortalBuybacksPage = lazy(() => import('@/pages/portal/PortalBuybacksPage'));
const PortalBuybackDetailPage = lazy(() =>
  import('@/pages/portal/PortalBuybacksPage').then((module) => ({
    default: module.PortalBuybackDetailPage,
  })),
);

const PortalInvoicePage = lazy(() =>
  import('@/pages/portal/PortalDocumentPages').then((module) => ({
    default: module.PortalInvoicePage,
  })),
);
const PortalQuotationPrintPage = lazy(() =>
  import('@/pages/portal/PortalDocumentPages').then((module) => ({
    default: module.PortalQuotationPrintPage,
  })),
);
const PortalReceiptPage = lazy(() =>
  import('@/pages/portal/PortalDocumentPages').then((module) => ({
    default: module.PortalReceiptPage,
  })),
);

export const portalRoutes: RouteObject[] = [
  {
    path: 'portal',
    children: [
      {
        element: <PortalGuestGuard />,
        children: [
          {
            element: <PortalAuthLayout />,
            children: [{ path: 'login', element: <PortalLoginPage /> }],
          },
        ],
      },
      {
        element: <PortalAuthGuard />,
        children: [
          {
            element: <PortalLayout />,
            children: [
              { index: true, element: <PortalDashboardPage /> },
              { path: 'profile', element: <PortalProfilePage /> },

              { path: 'quotations', element: <PortalQuotationsPage /> },
              { path: 'quotations/:id', element: <PortalQuotationDetailPage /> },

              // "Purchases" in the customer's language; the same sale document.
              { path: 'purchases', element: <PortalSalesPage /> },
              { path: 'purchases/:id', element: <PortalSaleDetailPage /> },

              { path: 'payments', element: <PortalPaymentsPage /> },

              { path: 'rentals', element: <PortalRentalsPage /> },
              { path: 'rentals/:id', element: <PortalRentalDetailPage /> },

              { path: 'buybacks', element: <PortalBuybacksPage /> },
              { path: 'buybacks/:id', element: <PortalBuybackDetailPage /> },

              // Phase 7 — the customer's messages, and the one thing they may
              // change about themselves. Preferences write preference rows and
              // nothing else; the customer record stays read-only.
              { path: 'notifications', element: <PortalNotificationsPage /> },
              { path: 'preferences', element: <PortalPreferencesPage /> },

              // The portal's own 404, never the ERP one.
              { path: '*', element: <PortalNotFoundPage /> },
            ],
          },
          /**
           * Print views sit outside `PortalLayout` so the portal header and nav
           * are not part of the printed page. Each still authorises on the
           * server through its own endpoint.
           */
          { path: 'purchases/:id/invoice', element: <PortalInvoicePage /> },
          { path: 'quotations/:id/print', element: <PortalQuotationPrintPage /> },
          { path: 'payments/:id/receipt', element: <PortalReceiptPage /> },
        ],
      },
    ],
  },
  // A bare `/portal/` with a trailing slash lands on the overview rather than a 404.
  { path: 'portal/', element: <Navigate to={PORTAL_ROUTES.dashboard} replace /> },
];
